import { Button, Descriptions, message, Modal, Space, Table, Image } from 'antd'
import { Ticket } from '../type/ticket'
import { translate } from '../../../core'
import { useMutation, useQuery } from '@apollo/client'
import {
  GetTicketQuery,
  GetNewTicketCountQuery,
  SetTicketAsSeenMutation,
  SetTicketAsRepliedMutation,
  TicketSendEmail,
  TicketSendEmailInput
} from '../graphql/ticket.graphql'
import { useState } from 'react'
import { dateDisplay, dateFormatAndTimeSlashed, resolvePathFile } from '../../../core/until'
import { InputCard } from '../../inventory/component/InputCard'
import { CheckCircleOutlined, PaperClipOutlined, MailOutlined, SettingOutlined, GroupOutlined } from '@ant-design/icons'
import { InfoTooltip, ShowIf } from '../../../core/component'
import { SendEmail } from '../../../core/component/SendEmail'
import { TicketCategoryList } from './TicketCategoryList'

export const TicketList = () => {
  const [messageApi, contextHolder] = message.useMessage()
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [openSendEmail, setOpenSendEmail] = useState<boolean>(false)
  const [openCategoryModal, setOpenCategoryModal] = useState<boolean>(false)
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
  const {data, loading} = useQuery(GetTicketQuery, {
    variables: {
      request: {
        take: pageSize,
        skip: currentPage * pageSize,
      }
    }
  })
  const [setAsSeen] = useMutation(SetTicketAsSeenMutation, {
    refetchQueries: [GetNewTicketCountQuery],
  })

  const [setAsReplied] = useMutation(SetTicketAsRepliedMutation, {
    refetchQueries: [GetTicketQuery],
  })

  const [sendTicketEmail, { loading: sendingTicketEmail }] = useMutation(
    TicketSendEmail,
    {
      onCompleted: () => {
        setOpenSendEmail(false)
        setSelectedTicket(null)
        messageApi.success(translate('email', 'sent'))
      },
      onError: (error) => messageApi.error(error.message),
      refetchQueries: [GetTicketQuery],
    }
  )

  return (
    <InputCard contained>
      <Space className="w-100 justify-content-between mb-2">
        <InfoTooltip contentKey='tickets.list' placement='right' />
        <Button
          icon={<GroupOutlined />}
          onClick={() => setOpenCategoryModal(true)}
        >
          {translate('categories')}
        </Button>
      </Space>
      
      <Table<Ticket>
        loading={loading}
        columns={[
          {
            dataIndex: 'imageId',
            width: 20,
            render: (value: string) => (
              <ShowIf
                condition={!!value}
                replacement={<></>}
              >
                <PaperClipOutlined />
              </ShowIf>
            ),
          },
          {
            dataIndex: 'id',
            title: translate('#'),
            width: 50,
          },
          {
            dataIndex: 'createdAt',
            title: translate('created_at'),
            width: 200,
            render: value => dateDisplay(value, dateFormatAndTimeSlashed),
          },
          {
            dataIndex: 'firstName',
            title: translate('first_name'),
            width: 150,
          },
          {
            dataIndex: 'lastName',
            title: translate('last_name'),
            width: 150,
          },
          {
            dataIndex: 'email',
            title: translate('email'),
            width: 200,
          },
          {
            dataIndex: 'category',
            title: translate('category'),
            width: 150,
            render: (category: { title: string }) => category?.title || '-',
          },
          {
            dataIndex: 'replied',
            title: translate('replied'),
            width: 100,
            render: (value: boolean) => (
              <ShowIf
                condition={value}
                replacement={<></>}
              >
                <CheckCircleOutlined style={{color: '#52c41a'}}/>
              </ShowIf>
            ),
          },
        ]}
        dataSource={data?.ticketsFind.data.map(it => ({key: it.id.toString(), ...it})) ?? []}
        pagination={{
          total: data?.ticketsFind.count,
          position: ['bottomCenter'],
          pageSize: pageSize,
          showSizeChanger: true,
        }}
        onChange={e => {
          if (e.current)
            setCurrentPage(e.current - 1)

          if (e.pageSize)
            setPageSize(e.pageSize)
        }}
        expandable={{
          onExpand: (expanded, record) => {
            if (!expanded)
              return

            if (record.seen)
              return

            return setAsSeen({
              variables: {
                id: record.id,
              }
            })

          },
          expandedRowRender: record =>
            <>
              <Descriptions
                layout='vertical'
                bordered
                items={[
                  {
                    key: '2',
                    label: translate('description'),
                    children: record.description || '-',
                  },
                  ...(record.imageId ? [{
                    key: '3',
                    label: translate('image'),
                    children: (
                      <Image
                        src={resolvePathFile(record.imageId ?? '')}
                        alt="Ticket attachment"
                        style={{ maxWidth: '300px', maxHeight: '300px' }}
                      />
                    ),
                  }] : []),
                ]}
              />
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="primary"
                  icon={<MailOutlined />}
                  onClick={() => {
                    setSelectedTicket(record)
                    setOpenSendEmail(true)
                  }}
                  disabled={record.replied}
                >
                  {translate('reply')}
                </Button>
              </div>
            </>
        }}
      />
      {contextHolder}

      <SendEmail
        open={openSendEmail}
        onCancel={() => {
          setOpenSendEmail(false)
          setSelectedTicket(null)
        }}
        onSend={(input) => {
          if (!selectedTicket) return
          
          const ticketEmailInput: TicketSendEmailInput = {
            ticketId: selectedTicket.id,
            subject: input.subject,
            text: input.text,
            attachments: input.attachments,
          }
          
          sendTicketEmail({
            variables: {
              input: ticketEmailInput,
            },
          })
        }}
        sending={sendingTicketEmail}
        attachment={{
          enabled: true,
          prefix: 'ticket',
        }}
        emailInputEnabled={{
          email: selectedTicket?.email,
        }}
      />

      <Modal
        title={translate('ticket', 'categories')}
        open={openCategoryModal}
        onCancel={() => setOpenCategoryModal(false)}
        footer={null}
        width="80%"
        maskClosable={false}
      >
        <TicketCategoryList />
      </Modal>
    </InputCard>
  )
}

